import { useState } from 'react';
import { useGeolocated } from 'react-geolocated';
import CalculateGps from '../utils/functions/CalculateGps';

/**
 * เช็ค GPS ก่อนอนุญาตให้ลงเวลาออก — ใช้ร่วมกันระหว่าง ListTimestamp.js (เช็คก่อนพาไปหน้า checkout)
 * และ CheckOut.js (เช็คซ้ำอีกชั้นกันกรณีเข้าหน้านี้ตรง ๆ)
 *
 * บังคับเช็คเฉพาะเมื่อครบทั้ง 3 เงื่อนไข:
 * 1) พนักงานคนนี้ "ตอนนี้" ตั้งค่าเป็นโหมดลงเวลาด้วย GPS อยู่ (เช็คค่าปัจจุบันจาก me.userData
 *    ทุกครั้ง ไม่ใช่ดูจาก record เก่า — ตรงกับ useLocationMode ใน CheckIn.js) พนักงานที่ตั้งค่า
 *    เป็นเลือกโครงการเองจะไม่โดน block เด็ดขาด ไม่ว่า record ที่จะเช็คเอาท์จะเป็นยังไง
 * 2) โครงการที่เช็คอินไว้เปิด checkout_gps_required
 * 3) record นี้เช็คอินด้วย GPS mode ไว้จริง — เช็คจาก currentLocation.lat แทนการเช็คแค่
 *    "locationCheckIn มีค่าไหม" เพราะ locationCheckIn.nearestProject.isNearby มี default:false
 *    ใน schema (Timestamp.js) ทำให้ Mongoose สร้าง object นี้ให้ทุก record อัตโนมัติแม้ไม่มีใครส่งมา
 *    (บั๊กเดียวกับ project_override)
 */
export default function useCheckoutGpsGuard(timestamp, me, info) {
  const [gpsBlockOpen, setGpsBlockOpen] = useState(false);
  const [gpsBlockReason, setGpsBlockReason] = useState('');

  const { coords, positionError } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 5 * 60 * 1000,
    },
    userDecisionTimeout: 5000,
  });

  // ค่าเดียวกับ useLocationMode ใน CheckIn.js — ต่อพนักงานก่อน ถ้าไม่ได้ตั้งไว้ค่อย fallback ไปตั้งค่าบริษัท
  const employeeUsesGpsMode =
    me?.userData?.timestamp_use_location != null
      ? !!me.userData.timestamp_use_location
      : !!info?.setting?.timestamp_location;

  const needsGpsCheck =
    employeeUsesGpsMode &&
    !!timestamp?.project_in?.checkout_gps_required &&
    timestamp?.locationCheckIn?.currentLocation?.lat != null;

  /** คืนค่า true ถ้าผ่าน (ทำต่อได้), false ถ้าติด block (เปิด dialog แจ้งเหตุผลไว้แล้ว) */
  const checkGpsForCheckout = () => {
    if (!needsGpsCheck) return true;

    if (positionError) {
      setGpsBlockReason(
        positionError.code === 1
          ? 'ไม่ได้อนุญาตให้เข้าถึงตำแหน่ง (Location) กรุณาเปิดสิทธิ์การเข้าถึงตำแหน่งในเบราว์เซอร์/มือถือ แล้วลองใหม่'
          : 'ไม่สามารถระบุตำแหน่งได้ (หาสัญญาณ GPS ไม่พบ) กรุณาลองใหม่ในบริเวณที่สัญญาณดีขึ้น',
      );
      setGpsBlockOpen(true);
      return false;
    }
    if (!coords) {
      setGpsBlockReason(
        'ยังไม่พบตำแหน่ง GPS ของคุณ กรุณาตรวจสอบว่าเปิด Location ไว้แล้วลองใหม่อีกครั้ง',
      );
      setGpsBlockOpen(true);
      return false;
    }
    const check = CalculateGps(coords, [timestamp?.project_in], 1000);
    if (!check?.nearestProject?.isNearby) {
      const distText = check?.nearestProject?.distanceText || '-';
      setGpsBlockReason(
        `คุณอยู่นอกพื้นที่โครงการ (ห่างประมาณ ${distText}) ไม่สามารถลงเวลาออกได้ กรุณาเข้าใกล้พื้นที่โครงการแล้วลองใหม่`,
      );
      setGpsBlockOpen(true);
      return false;
    }
    return true;
  };

  return {
    coords,
    positionError,
    needsGpsCheck,
    checkGpsForCheckout,
    gpsBlockOpen,
    setGpsBlockOpen,
    gpsBlockReason,
  };
}
